/**
 * Query-string state (SPEC 09): every view filter reads from and writes to
 * the address bar through one of these codecs, so a copied URL reproduces
 * the view. Pure: the hook in src/hooks/use-url-state.ts does the routing.
 */
import { parseRange, type HistoryRange } from '@/lib/app-detail/daily-buckets';
import { countryCode } from '@/lib/security/input';

/** Reads and writes the keys it owns. `null` in the serialized record drops the key: a default never appears in the URL. */
export type UrlCodec<T> = {
  keys: readonly string[];
  parse: (raw: Readonly<Record<string, string | null>>) => T;
  serialize: (value: T) => Readonly<Record<string, string | null>>;
};

export function readParams<T>(params: URLSearchParams | null | undefined, codec: UrlCodec<T>): T {
  return codec.parse(Object.fromEntries(codec.keys.map((k) => [k, params?.get(k) ?? null])));
}

/** A copy of `params` with the codec's keys set, or dropped where the value is the default. */
export function writeParams<T>(params: URLSearchParams | null | undefined, codec: UrlCodec<T>, value: NoInfer<T>): URLSearchParams {
  const next = new URLSearchParams(params?.toString() ?? '');
  for (const [k, v] of Object.entries(codec.serialize(value))) {
    if (v == null) next.delete(k);
    else next.set(k, v);
  }
  return next;
}

function single<T>(key: string, parse: (raw: string | null) => T, serialize: (value: T) => string | null): UrlCodec<T> {
  return { keys: [key], parse: (raw) => parse(raw[key] ?? null), serialize: (v) => ({ [key]: serialize(v) }) };
}

/** One of `values`; anything else is the fallback, and the fallback is dropped. */
export function enumParam<T extends string>(key: string, values: readonly T[], fallback: T): UrlCodec<T> {
  return single(key, (raw) => (values.includes(raw as T) ? (raw as T) : fallback), (v) => (v === fallback ? null : v));
}

/**
 * `range=30d|90d|1y|all`, validated by the history helper.
 *
 * This codec ALWAYS writes its value, even when the value equals its own
 * fallback, which is the one exception to the drop-the-default rule above. One
 * page mounts several range cards on the same `range` key with different
 * fallbacks: the rank chart and the storefront overlay default to `90d`, the
 * ratings, estimates, worldwide-ratings and price cards to `all`. On a shared
 * key an absent value does not mean "the default", it means "every card picks
 * its own". Measured on /app/chatgpt-6448311069 before this change: with the URL
 * at `?range=1y` and all seven toggles on 1y, clicking `90d` on the rank chart
 * deleted the key and moved the other five cards from 1y back to `all`, which
 * the reader never asked for.
 */
export function rangeParam(key: string, fallback: HistoryRange): UrlCodec<HistoryRange> {
  return single(key, (raw) => parseRange(raw, fallback), (v) => v);
}

/** `rc=gb`: a two-letter storefront; the page storefront is the default and is dropped. */
export function countryParam(key: string, fallback: string): UrlCodec<string> {
  return single(key, (raw) => (raw == null ? fallback : countryCode(raw, fallback)), (v) => (v === fallback ? null : v));
}

export type SortState<C extends string> = { col: C; dir: 'asc' | 'desc' };

/** `sort=<col>&dir=asc|desc` as one value, so a column change and its direction land in one navigation. */
export function sortParam<C extends string>(colKey: string, dirKey: string, cols: readonly C[], fallback: SortState<C>): UrlCodec<SortState<C>> {
  return {
    keys: [colKey, dirKey],
    parse: (raw) => ({
      col: cols.includes(raw[colKey] as C) ? (raw[colKey] as C) : fallback.col,
      dir: raw[dirKey] === 'asc' || raw[dirKey] === 'desc' ? raw[dirKey] : fallback.dir,
    }),
    serialize: (v) => ({ [colKey]: v.col === fallback.col ? null : v.col, [dirKey]: v.dir === fallback.dir ? null : v.dir }),
  };
}

/**
 * `6448311069,994459137` to distinct positive app ids, in the order given and uncapped.
 * Exported so a page that draws only the first N can still say how many were asked for
 * without writing a second, slightly different rule for what an id is.
 */
export function parseIdList(raw: string | null | undefined): number[] {
  const out: number[] = [];
  // A Set, not `out.includes`: this runs on whatever a crafted link puts in `?ids=`, and the
  // linear scan made it quadratic — 20,000 ids froze the tab before anything was drawn.
  const seen = new Set<number>();
  for (const part of (raw ?? '').split(',')) {
    const trimmed = part.trim();
    // `String(n) === trimmed` refuses "12abc", "+12" and a leading zero, which are not ids we store.
    const n = Number.parseInt(trimmed, 10);
    if (Number.isSafeInteger(n) && n > 0 && String(n) === trimmed && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

/** `ids=6448311069,994459137`: `parseIdList` capped at `max`; anything else is dropped. */
export function idsParam(key: string, max: number): UrlCodec<number[]> {
  return single(
    key,
    (raw) => parseIdList(raw).slice(0, max),
    (v) => (v.length ? v.slice(0, max).join(',') : null),
  );
}
